import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, Target, Search, Plus, Pencil, CheckCircle2, Circle, Save, X,
} from 'lucide-react';
import {
  atlasApi, AtlasUser, Pdi, PdiObjetivo, PdiAcao, PdiObjetivoStatus, PdiAcaoTipo,
} from '../shared/atlasApi';
import FeedbackBanner, { Feedback } from '../shared/FeedbackBanner';
import ListaEditavel, { inputCls, labelCls } from '../shared/ListaEditavel';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
  funccodi?: number;
  // Modo embutido (ficha): esconde a busca de funccodi.
  fixo?: boolean;
}

const STATUS_OBJ: Record<PdiObjetivoStatus, { label: string; classes: string }> = {
  aberto: { label: 'Aberto', classes: 'bg-gray-100 text-gray-600' },
  em_andamento: { label: 'Em andamento', classes: 'bg-blue-100 text-blue-700' },
  concluido: { label: 'Concluído', classes: 'bg-emerald-100 text-emerald-700' },
  cancelado: { label: 'Cancelado', classes: 'bg-rose-100 text-rose-700' },
};

const ACAO_LABEL: Record<PdiAcaoTipo, string> = {
  treinamento: 'Treinamento', leitura: 'Leitura', mentoria: 'Mentoria', projeto: 'Projeto',
};

function novaAcao(): PdiAcao {
  return { id: `a${Date.now()}${Math.floor(Math.random() * 1000)}`, tipo: 'treinamento', descricao: '', concluida_em: null };
}
function novoObjetivo(): PdiObjetivo {
  return { id: `o${Date.now()}${Math.floor(Math.random() * 1000)}`, titulo: '', descricao: '', prazo: '', status: 'aberto', acoes: [] };
}

function fdata(s?: string | null): string {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
}

const PdiApp: React.FC<Props> = ({ user, podeEditar, funccodi, fixo }) => {
  const [funcAtivo, setFuncAtivo] = useState<number | null>(funccodi ?? null);
  const [funcInput, setFuncInput] = useState<string>(funccodi ? String(funccodi) : '');

  const [pdi, setPdi] = useState<Pdi | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [editando, setEditando] = useState(false);
  const [draft, setDraft] = useState<PdiObjetivo[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [fb, setFb] = useState<Feedback>(null);
  const [acaoBusy, setAcaoBusy] = useState<string | null>(null);

  const carregar = useCallback((fc: number) => {
    setLoading(true);
    setErro(null);
    setEditando(false);
    atlasApi.getPdi(user, fc)
      .then(r => setPdi(r))
      .catch(e => setErro(e.message || 'Erro ao carregar PDI'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (funccodi != null) { setFuncAtivo(funccodi); carregar(funccodi); }
  }, [funccodi, carregar]);

  const buscar = () => {
    const fc = parseInt(funcInput, 10);
    if (isNaN(fc)) { setErro('Informe um funccodi válido.'); return; }
    setFuncAtivo(fc);
    carregar(fc);
  };

  const iniciarEdicao = () => {
    setDraft((pdi?.objetivos || []).map(o => ({ ...o, acoes: (o.acoes || []).map(a => ({ ...a })) })));
    setFb(null);
    setEditando(true);
  };

  const salvar = async () => {
    if (funcAtivo == null) return;
    const objetivos = draft
      .filter(o => o.titulo.trim())
      .map(o => ({
        ...o,
        titulo: o.titulo.trim(),
        descricao: o.descricao?.trim() || undefined,
        prazo: o.prazo || undefined,
        acoes: (o.acoes || []).filter(a => a.descricao.trim()).map(a => ({ ...a, descricao: a.descricao.trim() })),
      }));
    setSalvando(true);
    setFb(null);
    try {
      await atlasApi.salvarPdi(user, funcAtivo, { ciclo_origem_id: pdi?.ciclo_origem_id ?? undefined, objetivos });
      setEditando(false);
      carregar(funcAtivo);
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const toggleAcao = async (objId: string, acao: PdiAcao) => {
    if (funcAtivo == null || acaoBusy) return;
    const concluir = !acao.concluida_em;
    setAcaoBusy(acao.id);
    try {
      await atlasApi.concluirAcaoPdi(user, funcAtivo, acao.id, concluir);
      setPdi(prev => prev ? {
        ...prev,
        objetivos: prev.objetivos.map(o => o.id !== objId ? o : {
          ...o,
          acoes: o.acoes.map(a => a.id !== acao.id ? a : { ...a, concluida_em: concluir ? new Date().toISOString() : null }),
        }),
      } : prev);
    } catch (e: any) {
      setErro(e.message || 'Erro ao atualizar ação.');
    } finally {
      setAcaoBusy(null);
    }
  };

  return (
    <div className={fixo ? 'space-y-4' : 'p-6 space-y-4 max-w-4xl'}>
      {!fixo && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center"><Target className="w-5 h-5" /></div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-gray-900">PDI — Plano de Desenvolvimento Individual</h2>
            <p className="text-sm text-gray-500">Objetivos e ações de desenvolvimento por colaborador.</p>
          </div>
        </div>
      )}

      {!fixo && (
        <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <label className={labelCls}>Funccodi do colaborador</label>
            <input className={inputCls} value={funcInput} onChange={e => setFuncInput(e.target.value)} placeholder="12534" onKeyDown={e => { if (e.key === 'Enter') buscar(); }} />
          </div>
          <button onClick={buscar} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2 h-[38px]">
            <Search className="w-4 h-4" /> Buscar
          </button>
        </div>
      )}

      {loading && <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando PDI...</div>}
      {erro && <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">{erro}</div>}

      {!loading && !erro && funcAtivo == null && (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-400">Informe um colaborador para ver o PDI.</div>
      )}

      {!loading && !erro && pdi && !editando && (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1 text-sm text-gray-500">
              {(pdi.objetivos || []).length} objetivo{(pdi.objetivos || []).length !== 1 ? 's' : ''}
              {pdi.criado_em && ` · criado em ${fdata(pdi.criado_em)}`}
            </div>
            {podeEditar && (
              <button onClick={iniciarEdicao} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2">
                {(pdi.objetivos || []).length === 0 ? <Plus className="w-4 h-4" /> : <Pencil className="w-4 h-4" />} {(pdi.objetivos || []).length === 0 ? 'Criar PDI' : 'Editar'}
              </button>
            )}
          </div>

          {(pdi.objetivos || []).length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-400">Nenhum objetivo de desenvolvimento cadastrado.</div>
          ) : (
            <ul className="space-y-3">
              {pdi.objetivos.map(o => {
                const st = STATUS_OBJ[o.status] || STATUS_OBJ.aberto;
                return (
                  <li key={o.id} className="bg-white rounded-2xl border border-gray-200 p-5">
                    <div className="flex items-start gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900">{o.titulo}</h3>
                        {o.descricao && <p className="text-sm text-gray-500 mt-0.5">{o.descricao}</p>}
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${st.classes}`}>{st.label}</span>
                      {o.prazo && <span className="text-xs text-gray-400">prazo {fdata(o.prazo)}</span>}
                    </div>
                    {(o.acoes || []).length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        {o.acoes.map(a => {
                          const ok = !!a.concluida_em;
                          return (
                            <div key={a.id} className="flex items-start gap-3 p-2.5 rounded-xl border border-gray-100 bg-gray-50/50">
                              <button
                                type="button"
                                onClick={() => toggleAcao(o.id, a)}
                                disabled={!!acaoBusy}
                                title={ok ? 'Reabrir' : 'Concluir'}
                                className={`mt-0.5 flex-shrink-0 ${ok ? 'text-emerald-600' : 'text-gray-300 hover:text-purple-500'}`}
                              >
                                {acaoBusy === a.id ? <Loader2 className="w-5 h-5 animate-spin" /> : ok ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm ${ok ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{a.descricao}</div>
                                <div className="text-[11px] text-gray-400">
                                  {ACAO_LABEL[a.tipo] || a.tipo}
                                  {ok && a.concluida_em && ` · concluída ${fdata(a.concluida_em)}`}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {/* Edição */}
      {editando && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-gray-900 flex-1">Editar objetivos</h3>
            <button onClick={() => setEditando(false)} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100"><X className="w-4 h-4" /></button>
          </div>

          <ListaEditavel<PdiObjetivo>
            itens={draft}
            onChange={setDraft}
            novoItem={novoObjetivo}
            rotuloAdd="adicionar objetivo"
            vazioMsg="Nenhum objetivo."
            renderLinha={(o, atualizar) => (
              <div className="space-y-2">
                <input className={inputCls} placeholder="Título do objetivo" value={o.titulo} onChange={e => atualizar({ titulo: e.target.value })} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <select className={inputCls} value={o.status} onChange={e => atualizar({ status: e.target.value as PdiObjetivoStatus })}>
                    {(Object.keys(STATUS_OBJ) as PdiObjetivoStatus[]).map(k => <option key={k} value={k}>{STATUS_OBJ[k].label}</option>)}
                  </select>
                  <input className={inputCls} type="date" value={o.prazo || ''} onChange={e => atualizar({ prazo: e.target.value })} />
                </div>
                <input className={inputCls} placeholder="Descrição (opcional)" value={o.descricao || ''} onChange={e => atualizar({ descricao: e.target.value })} />
                <div>
                  <label className={labelCls}>Ações</label>
                  <ListaEditavel<PdiAcao>
                    itens={o.acoes || []}
                    onChange={acoes => atualizar({ acoes })}
                    novoItem={novaAcao}
                    rotuloAdd="adicionar ação"
                    vazioMsg="Nenhuma ação."
                    renderLinha={(a, atualizarAcao) => (
                      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2">
                        <select className={inputCls} value={a.tipo} onChange={e => atualizarAcao({ tipo: e.target.value as PdiAcaoTipo })}>
                          {(Object.keys(ACAO_LABEL) as PdiAcaoTipo[]).map(k => <option key={k} value={k}>{ACAO_LABEL[k]}</option>)}
                        </select>
                        <input className={inputCls} placeholder="Descrição da ação" value={a.descricao} onChange={e => atualizarAcao({ descricao: e.target.value })} />
                      </div>
                    )}
                  />
                </div>
              </div>
            )}
          />

          <FeedbackBanner feedback={fb} />

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setEditando(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button onClick={salvar} disabled={salvando} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
              {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar PDI
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdiApp;
